import React, { useEffect, useRef, useState } from 'react';
import init, { predict } from 'brique-demo-wasm';
import './App.css';
import { Button } from '@mantine/core';
import {
  MNIST_IMAGE_SIZE,
  preprocessAlphaBitmap,
} from './mnistPreprocessing';

export const DRAWING_CANVAS_SIZE = MNIST_IMAGE_SIZE * 10;
export const DRAWING_PEN_WIDTH = DRAWING_CANVAS_SIZE / 10;

interface DrawingCanvasProps {
  width: number;
  height: number;
  penWidth: number;
  setProb: (newProb: Float64Array) => void;
}

type Point = {
  x: number;
  y: number;
};

async function processModel(): Promise<Uint8Array> {
  const modelPath = `${process.env.PUBLIC_URL || ''}/mnist_128x128_0.9775.brq`;
  const response = await fetch(modelPath);
  if (!response.ok) {
    throw new Error(`Failed to load model: ${response.status}`);
  }

  const model = new Uint8Array(await response.arrayBuffer());
  const magic = [67, 79, 79, 75, 73, 69];
  const hasValidMagic = magic.every((value, index) => model[index] === value);
  let declaredLength = 0;

  for (let index = 7; index < 15 && index < model.length; index += 1) {
    declaredLength = declaredLength * 256 + model[index];
  }

  if (
    model.length < 15 ||
    !hasValidMagic ||
    model[6] !== 2 ||
    declaredLength !== model.length
  ) {
    throw new Error('The downloaded model is not a valid Brique.rs model');
  }

  return model;
}

export default function DrawingCanvas(props: DrawingCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef<Point | null>(null);
  const [model, setModel] = useState<Uint8Array>();
  const [modelError, setModelError] = useState<string>();

  useEffect(() => {
    let isActive = true;

    processModel()
      .then((loadedModel) => {
        if (isActive) {
          setModel(loadedModel);
          setModelError(undefined);
        }
      })
      .catch((error: unknown) => {
        console.error(error);
        if (isActive) {
          setModelError('The model could not be loaded.');
        }
      });

    return () => {
      isActive = false;
    };
  }, []);

  function startDrawing(point: Point): void {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    isDrawingRef.current = true;
    lastPointRef.current = point;
    ctx.fillStyle = '#000000';
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = props.penWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Draw taps as dots and make the start of every stroke visible.
    ctx.beginPath();
    ctx.arc(point.x, point.y, props.penWidth / 2, 0, Math.PI * 2);
    ctx.fill();
  }

  function draw(point: Point): void {
    if (!isDrawingRef.current) return;

    const canvas = canvasRef.current;
    const previousPoint = lastPointRef.current;
    if (!canvas || !previousPoint) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.strokeStyle = '#000000';
    ctx.lineWidth = props.penWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(previousPoint.x, previousPoint.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();

    lastPointRef.current = point;
  }

  function stopDrawing(): void {
    isDrawingRef.current = false;
    lastPointRef.current = null;
  }

  function clear(): void {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    stopDrawing();
  }

  function getModelInput(): Float64Array {
    const canvas = canvasRef.current;
    if (!canvas) return new Float64Array(MNIST_IMAGE_SIZE * MNIST_IMAGE_SIZE);
    const ctx = canvas.getContext('2d');
    if (!ctx) return new Float64Array(MNIST_IMAGE_SIZE * MNIST_IMAGE_SIZE);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const alphaBitmap = new Uint8Array(canvas.width * canvas.height);

    for (
      let pixel = 0, dataIndex = 3;
      pixel < alphaBitmap.length;
      pixel += 1, dataIndex += 4
    ) {
      alphaBitmap[pixel] = imageData.data[dataIndex];
    }

    return preprocessAlphaBitmap(alphaBitmap, canvas.width, canvas.height);
  }

  async function modelEvaluation(): Promise<void> {
    if (!model) return;

    try {
      const input = getModelInput();
      const expectedInputLength = MNIST_IMAGE_SIZE * MNIST_IMAGE_SIZE;

      if (
        input.length !== expectedInputLength ||
        !Array.from(input).every((value) => Number.isFinite(value))
      ) {
        throw new Error('The processed drawing is not a valid MNIST input');
      }

      await init();
      props.setProb(predict(model, input));
      setModelError(undefined);
    } catch (error: unknown) {
      console.error('Prediction failed', error);
      setModelError('Prediction failed. Please redraw and try again.');
    }
  }

  function getPointFromPointer(
    event: React.PointerEvent<HTMLCanvasElement>
  ): Point {
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / canvas.clientWidth;
    const scaleY = canvas.height / canvas.clientHeight;

    return {
      x: (event.clientX - rect.left - canvas.clientLeft) * scaleX,
      y: (event.clientY - rect.top - canvas.clientTop) * scaleY,
    };
  }

  function handlePointerDown(
    event: React.PointerEvent<HTMLCanvasElement>
  ): void {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    startDrawing(getPointFromPointer(event));
  }

  function handlePointerMove(
    event: React.PointerEvent<HTMLCanvasElement>
  ): void {
    if (!isDrawingRef.current) return;
    event.preventDefault();
    draw(getPointFromPointer(event));
  }

  function handlePointerUp(
    event: React.PointerEvent<HTMLCanvasElement>
  ): void {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    stopDrawing();
  }

  return (
    <div className="drawingCard">
      <canvas
        ref={canvasRef}
        width={DRAWING_CANVAS_SIZE}
        height={DRAWING_CANVAS_SIZE}
        style={{ width: props.width, height: props.height }}
        className="drawingCanva"
        aria-label="Draw a digit"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      />
      <div className="btns">
        <Button variant="default" onClick={clear}>Clear</Button>
        <Button onClick={modelEvaluation} disabled={!model}>Predict</Button>
      </div>
      {modelError && <p className="model-error" role="alert">{modelError}</p>}
    </div>
  );
}
